import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Check, Zap } from 'lucide-react';
import { loadToken } from '../api';

const plans = [
  {
    name: 'Free',
    pack: null,
    priceDisplay: 'Free',
    priceNote: null,
    credits: '10',
    note: 'Try the API — no credit card required',
    cta: 'Get Started Free',
    popular: false,
    features: [
      '10 bonus credits on sign-up',
      'Raw OCR: 1 credit/page',
      'Field extraction: 3 credits/page',
      'PDF, PNG, JPEG, WEBP, TIFF',
      'Up to 10 pages per file',
      'JSON response',
      'Community support',
    ],
  },
  {
    name: 'Starter',
    pack: 'starter',
    priceDisplay: '$5',
    priceNote: 'one-time purchase',
    credits: '1,000',
    note: 'For individuals and small teams',
    cta: 'Buy Starter',
    popular: false,
    features: [
      '1,000 credits',
      'Raw OCR: 1 credit/page',
      'Field extraction: 3 credits/page',
      'All file formats supported',
      'Up to 10 pages per file',
      'API key management',
      'Email support',
    ],
  },
  {
    name: 'Pro',
    pack: 'pro',
    priceDisplay: '$15',
    priceNote: 'one-time purchase',
    credits: '5,000',
    note: 'Best for growing businesses',
    cta: 'Buy Pro',
    popular: true,
    features: [
      '5,000 credits',
      'All Starter features',
      'Priority processing queue',
      'Usage analytics dashboard',
      'Higher rate limits',
      'Priority email support',
    ],
  },
  {
    name: 'Business',
    pack: 'business',
    priceDisplay: '$40',
    priceNote: 'one-time purchase',
    credits: '20,000',
    note: 'For large-scale operations',
    cta: 'Buy Business',
    popular: false,
    features: [
      '20,000 credits',
      'All Pro features',
      'Bulk processing discount',
      'Higher rate limits',
      'Priority email support',
      'Dedicated account manager',
    ],
  },
];

export default function PricingSection({ onSelectPlan, onGetStarted, onSignIn }) {
  const [ref, visible] = useScrollAnimation();

  return (
    <section id='pricing' ref={ref} className={cn('py-24 bg-background reveal', visible && 'revealed')}>
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
        {/* Header */}
        <div className='text-center max-w-2xl mx-auto mb-16'>
          <span className='inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-accent/10 text-accent-dark border border-accent/20 mb-4'>
            Pricing
          </span>
          <h2 className='font-heading font-bold text-4xl lg:text-5xl text-foreground mb-4'>
            Simple, transparent pricing
          </h2>
          <p className='text-muted-foreground text-lg'>
            No hidden fees. Cancel or upgrade anytime.
          </p>
        </div>

        {/* Cards */}
        <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch'>
          {plans.map((plan, i) => (
            <div
              key={plan.name}
              className={cn(
                'relative rounded-2xl flex flex-col reveal',
                `reveal-delay-${(i % 3) + 1}`,
                visible && 'revealed',
                plan.popular
                  ? 'bg-gradient-cta shadow-float border-2 border-primary'
                  : 'bg-card shadow-card border border-border hover:shadow-primary transition-shadow duration-300'
              )}
            >
              {/* Popular badge */}
              {plan.popular && (
                <div className='absolute -top-3.5 left-1/2 -translate-x-1/2'>
                  <div className='flex items-center gap-1.5 bg-accent text-accent-foreground text-xs font-bold px-4 py-1.5 rounded-full shadow-accent'>
                    <Zap className='w-3.5 h-3.5' />
                    Most Popular
                  </div>
                </div>
              )}

              <div className='p-8 flex flex-col flex-1'>
                {/* Plan header */}
                <div className='mb-6'>
                  <h3 className={cn(
                    'font-heading font-bold text-xl mb-1',
                    plan.popular ? 'text-primary-foreground' : 'text-foreground'
                  )}>{plan.name}</h3>
                  <p className={cn(
                    'text-sm',
                    plan.popular ? 'text-primary-foreground/65' : 'text-muted-foreground'
                  )}>{plan.note}</p>
                </div>

                {/* Price */}
                <div className='mb-2'>
                  <div className='flex items-end gap-1'>
                    <span className={cn(
                      plan.priceNote ? 'font-heading font-bold text-2xl' : 'font-heading font-bold text-5xl',
                      plan.popular ? 'text-primary-foreground' : 'text-foreground'
                    )}>
                      {plan.priceDisplay}
                    </span>
                  </div>
                  {plan.priceNote && (
                    <p className={cn(
                      'text-xs mt-0.5',
                      plan.popular ? 'text-primary-foreground/60' : 'text-muted-foreground'
                    )}>{plan.priceNote}</p>
                  )}
                  <div className={cn(
                    'inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full mt-1',
                    plan.popular
                      ? 'bg-primary-foreground/15 text-primary-foreground'
                      : 'bg-credit/10 text-credit'
                  )}>
                    <span className='font-heading font-bold'>{plan.credits}</span>
                    <span>credits included</span>
                  </div>
                </div>

                {/* Features */}
                <ul className='space-y-3 my-6 flex-1'>
                  {plan.features.map((f) => (
                    <li key={f} className='flex items-start gap-2.5'>
                      <div className={cn(
                        'w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5',
                        plan.popular ? 'bg-accent text-accent-foreground' : 'bg-success/15 text-success'
                      )}>
                        <Check className='w-3 h-3' strokeWidth={2.5} />
                      </div>
                      <span className={cn(
                        'text-sm',
                        plan.popular ? 'text-primary-foreground/85' : 'text-muted-foreground'
                      )}>{f}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <div className='mt-auto'>
                  <Button
                    className='w-full'
                    size='lg'
                    variant={plan.popular ? 'accent' : 'outline-primary'}
                    onClick={() => {
                      if (!plan.pack) return onGetStarted();
                      // Paid plan — must be logged in first
                      if (!loadToken()) return onSignIn ? onSignIn() : onGetStarted();
                      // Logged in → go to dashboard upgrade tab
                      onSelectPlan(plan);
                    }}
                  >
                    {plan.cta}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer note */}
        <p className='text-center text-sm text-muted-foreground mt-8'>
          Free plan includes 10 bonus credits on sign-up. No credit card required.
        </p>
      </div>
    </section>
  );
}
